<script setup lang="ts">
import { productBookingsApi } from '@/api/product-bookings';
import { computed, inject, ref } from 'vue';
import { add, formatDistanceToNowStrict } from 'date-fns';
import { transbankApi } from '@/api/transbank';
import SuccessModal from '../success-modal.vue';
import FailureModal from '../failure-modal.vue';

interface Props {
  productBooking: ProductBooking
}

const user = inject<User>('user');
const props = defineProps<Props>();

const loading = ref(false);
const error = ref(false);

const paymentTimeLimit = inject<number>('paymentTimeLimit');

const showSuccessModal = ref(false);

function toggleSuccessModal() {
  showSuccessModal.value = !showSuccessModal.value;
}

const showFailureModal = ref(false);

function toggleFailureModal() {
  showFailureModal.value = !showFailureModal.value;
}
// eslint-disable-next-line vue/return-in-computed-property
const remainingTime = computed(() => formatDistanceToNowStrict(add(
  new Date(props.productBooking.updatedAt),
  { hours: paymentTimeLimit },
)));

const showCancelModal = ref(false);

function toggleCancelModal() {
  showCancelModal.value = !showCancelModal.value;
}

async function cancelBooking() {
  try {
    loading.value = true;
    await productBookingsApi.update(props.productBooking.id, { canceled: true, canceledBy: 'client' })
      .then(() => {
        window.location.reload();
      });
  } catch (e) {
    error.value = true;
  } finally {
    loading.value = false;
  }
}

const showVerifyModal = ref(false);
function toggleVerifyModal() {
  showVerifyModal.value = !showVerifyModal.value;
}

const transbankForm = ref();
// const transbankData = ref<PaymentData>();

const oneClickToken = ref();
const oneClickUrl = ref();

function handleTransbankOneClick() {
  loading.value = true;
  if (user?.transbankInscription) {
    transbankApi.payBooking(props.productBooking.id)
      .then((response) => {
        const payment = response.data.payment as Payment;
        loading.value = false;

        if (payment.status === 'authorized') {
          toggleSuccessModal();
        } else {
          toggleFailureModal();
        }
      });
  } else {
    transbankApi.createInscription()
      .then((response) => {
        oneClickToken.value = response.token;
        oneClickUrl.value = response.urlWebpay;
      })
      .then(() => {
        transbankForm.value.requestSubmit();
      });
  }
}
function handlePay() {
  if (user?.verified) {
    handleTransbankOneClick();
  } else {
    toggleVerifyModal();
  }
}

function handleSuccess() {
  window.location.href = '/perfil/solicitudes_reservas/cliente#reservados';
  window.location.reload();
}

function handleFailure() {
  toggleFailureModal();
}
</script>
<template>
  <div class="flex flex-col">
    <base-tip
      title="Tiempo restante"
      variant="bold"
    >
      Te quedan <span class="font-semibold">{{ remainingTime }}</span> para pagar esta reserva. Cumplido el tiempo, se cancelará automáticamente.
    </base-tip>
    <base-button
      class="mt-3"
      size="sm"
      :disabled="loading"
      @click="handlePay"
    >
      <img
        v-if="loading"
        src="@/assets/icons/loading-alt.svg"
        class="w-5 animate-spin-slow"
      >
      <p v-else>
        Pagar con OneClick
      </p>
    </base-button>
    <div
      class="-mt-1 flex justify-between rounded-b border-x border-b border-z-gray-800 px-1 pb-1 pt-1.5 text-sm text-z-gray-700"
    >
      <template v-if="user?.transbankInscription">
        <p>{{ user.transbankInscription.cardType }}</p>
        <p>•••• {{ user.transbankInscription.cardNumber.slice(-4) }}</p>
      </template>
      <template v-else>
        <p class="w-full text-center">
          Deberás inscribir tu cuenta
        </p>
      </template>
    </div>
    <base-button
      variant="danger"
      size="sm"
      class="mt-3"
      @click="toggleCancelModal"
    >
      Cancelar reserva
    </base-button>
    <base-modal :open="showCancelModal">
      <div class="relative flex w-full flex-col items-center">
        <button
          class="absolute right-0 top-0"
          @click="toggleCancelModal"
        >
          <img src="@/assets/icons/close.svg">
        </button>
        <img
          src="@/assets/icons/warning-alt-2.svg"
          class="h-20 w-20"
        >
        <p class="mt-2 text-center text-lg font-bold text-z-gray-900">
          ¿Estás seguro que deseas cancelar tu solicitud?
        </p>
        <base-button
          class="mt-4 w-full"
          variant="danger"
          @click="cancelBooking"
        >
          Cancelar solicitud
        </base-button>
      </div>
    </base-modal>
  </div>
  <base-modal :open="showVerifyModal">
    <div class="flex w-full flex-col items-center">
      <img
        src="@/assets/icons/information-circle.svg"
        class="h-20 w-20"
      >
      <p class="mt-2 text-lg font-bold text-z-gray-900">
        Antes de ello...
      </p>
      <p class="mt-2 text-center text-z-gray-800">
        Por motivos de seguridad, debes verificar tu identidad.
      </p>
      <base-button
        href="/perfil/verificar?redirect_to=/perfil/solicitudes_reservas/cliente%23esperando-pago"
        class="mt-4 w-full"
      >
        <p>
          Verificar
        </p>
        <img
          src="@/assets/icons/arrow-small-right.svg"
          class="ml-2"
        >
      </base-button>
    </div>
  </base-modal>
  <form
    v-if="!user?.transbankInscription"
    id="transbank-form"
    ref="transbankForm"
    method="post"
    :action="oneClickUrl"
  >
    <input
      type="hidden"
      name="TBK_TOKEN"
      :value="oneClickToken"
    >
  </form>
  <success-modal
    :open="showSuccessModal"
    description="Tu pago se ha acreditado de forma exitosa!"
    @confirm="handleSuccess"
  />
  <failure-modal
    :open="showFailureModal"
    description="Ha ocurrido un error al realizar tu pago. Por favor inténtalo nuevamente"
    @confirm="handleFailure"
  />
</template>
